import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CookiesProvider } from 'react-cookie';
import GlobalStyle from './utils/style/GlobalStyle';
import { ThemeProvider } from 'styled-components';
import { theme } from 'utils/style/theme';
import AuthRoute from 'AuthRoute';
import LocalizingContext from 'context/LocalizingContext';
import { fetchLocalizingList } from 'apis/common/commonApis';
import SidebarScrollState from 'components/layout/sidebar/SidebarScrollState';
// 접속 검증
import JoinPage from 'pages/login/JoinPage';
import NoPermissionPage from 'pages/login/NoPermissionPage';
import OrganizationInputPage from 'pages/login/OrganizaionInputPage';
import ContactUsList from 'pages/operation/contactUs/ContactUsList';
// 작품 관리
import ProjectTotalListPage from 'pages/story/project/ProjectTotalListPage';
import ProjectDetailPage from 'pages/story/project/ProjectDetailPage';

import MinicutUpdatePage from 'pages/story/resource/minicut/MinicutUpdatePage';
import IllustUpdatePage from 'pages/story/resource/illust/IllustUpdatePage';

import SystemSoundListPage from 'pages/platform/systemSound/SystemSoundListPage';
import PlatformImageListPage from 'pages/operation/platformImage/PlatformImageListPage';
import PlatformSoundListPage from 'pages/operation/platformSound/PlatformSoundListPage';
import ExchangeUpdatePage from 'pages/operation/exchange/ExchangeUpdatePage';
import ManagerListPage from 'pages/manager/ManagerListPage';
import ManagerDetailPage from 'pages/manager/ManagerDetailPage';
import PremiumPassListPage from 'pages/sales/premiumpass/PremiumPassListPage';

import PromotionListPage from 'pages/operation/promotion/PromotionListPage';
// 플랫폼 관리
import StoryDeployListPage from 'pages/platform/deploy/StoryDeployListPage';
import BubbleMainListPage from 'pages/platform/bubble/BubbleMainListPage';
import BubbleSetTabsPage from 'pages/platform/bubble/BubbleSetTabsPage';
import SpriteLIstPage from 'pages/platform/sprite/SpriteLIstPage';

import AdvertisementSettingPage from 'pages/sales/advertisement/AdvertisementSettingPage';
import AdCompensationPage from 'pages/operation/adCompensation/AdCompensationPage';

import SurveyListPage from 'pages/operation/survey/SurveyListPage';
import IfyouPassListPage from 'pages/sales/ifyouPass/IfyouPassListPage';
import AttendanceListPage from 'pages/operation/attendance/AttendanceListPage';
// 운영 관리
import UserListPage from 'pages/operation/user/UserListPage';

import NoticeListPage from 'pages/operation/notice/NoticeListPage';
import ComingSoonInputPage from 'pages/operation/comingSoon/ComingSoonInputPage';
// 판매 관리
import InAppStoreInputPage from 'pages/sales/inappStore/InAppStoreInputPage';
import CoinStoreList from 'pages/sales/coinStore/CoinStoreListPage';
import ComingSoonListPage from 'pages/operation/comingSoon/ComingSoonListPage';
import AttendanceInputPage from 'pages/operation/attendance/AttendanceInputPage';
import CurrencyListPage from 'pages/platform/currency/CurrencyListPage';
import CurrencyInputPage from 'pages/platform/currency/CurrencyInputPage';
import CoinStoreInputPage from 'pages/sales/coinStore/CoinStoreInputPage';
import PackageListPage from 'pages/package/PackageListPage';
import PackageDetailPage from 'pages/package/PackageDetailPage';
import PromotionInputPage from 'pages/operation/promotion/PromotionInputPage';
import DLCUpdatePage from 'pages/story/dlc/DLCUpdatePage';
import DLCEpisodeUpdatePage from 'pages/story/dlc/episode/DLCEpisodeUpdatePage';
import Login from 'pages/login';
import Signup from 'pages/signup';
import VerifyEmail from 'pages/verifyEmail';
import OtomeProjectEditPage from 'pages/story/project/otome/OtomeProjectEditPage';
import EpisodeEditPage from 'pages/story/episode/EpisodeEditPage';
import ScriptSheet from 'pages/story/script/ScriptSheet';
import LiveBackgroundUpdate from 'pages/story/resource/liveBackground/LiveBackgroundUpdate';
import LiveObjectUpdate from 'pages/story/resource/liveObject/LiveObjectUpdate';
import LiveIllustUpdate from 'pages/story/resource/liveIllust/LiveIllustUpdate';
import CharacterEditPage from 'pages/story/characters/CharacterEditPage';

import AssetsPage from 'pages/assets/AssetsPage';

import LoadingImageEditPage from 'pages/story/loadingImage/LoadingImageEditPage';
import ItemEdit from 'pages/story/operation/ItemEdit/ItemEdit';
import AbilityEditPage from 'pages/story/characters/AbilityEditPage';
import CreateOrganization from './pages/createOrganization';
import ProjectAuthRoute from './ProjectAuthRoute';
import NewNoticeInputPage from './pages/operation/notice/NewNoticeInputPage';
import CouponInputPage from './pages/operation/coupon/CouponInputPage';
import LocalizingPage from 'pages/localizing/LocalizingPage';

// 클라이언트 생성
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 360000,

      onError: (err) => {
        console.log('쿼리 에러:', err);
      },
    },
  },
});

const App = () => {
  const [langList, setLangList] = useState<
    { code: string; code_name: string; comment: string }[] | undefined
  >();

  const fetchData = async () => {
    const resLangList = await fetchLocalizingList();
    setLangList(resLangList);
  };

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <CookiesProvider>
          <LocalizingContext.Provider value={langList}>
            <GlobalStyle />
            <BrowserRouter>
              <SidebarScrollState />
              <Routes>
                <Route path='/login' element={<Login />} />
                <Route path='/signup' element={<Signup />} />
                <Route
                  path='/create-organization'
                  element={<CreateOrganization />}
                />
                <Route path='/verify/:email' element={<VerifyEmail />} />
                <Route path='join' element={<JoinPage />} />
                <Route
                  path='inputInfoDetail'
                  element={<OrganizationInputPage />}
                />
                <Route path='permission' element={<NoPermissionPage />} />

                {/* 작품 관리 */}
                <Route
                  path='/'
                  element={
                    <AuthRoute>
                      <ProjectTotalListPage />
                    </AuthRoute>
                  }
                >
                  <Route
                    path='/story'
                    element={
                      <AuthRoute>
                        <ProjectTotalListPage />
                      </AuthRoute>
                    }
                  />
                </Route>
                {/* 작품 관리 - 작품 상세 정보(작품카드, 에피소드 리스트) */}
                <Route
                  path='/story/:projectId/:pageType'
                  element={
                    <ProjectAuthRoute>
                      <ProjectDetailPage />
                    </ProjectAuthRoute>
                  }
                ></Route>
                <Route
                  path='/story/:projectId/:type/:dlcId'
                  element={
                    <ProjectAuthRoute>
                      <ProjectDetailPage dlcMenu={true} />
                    </ProjectAuthRoute>
                  }
                ></Route>

                {/* 작품 관리 - 작품 수정 */}
                <Route
                  path='/story/:projectId/otome/update'
                  element={
                    <AuthRoute>
                      <OtomeProjectEditPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='/story/:projectId/operation/item/:itemId/update'
                  element={
                    <AuthRoute>
                      <ItemEdit />
                    </AuthRoute>
                  }
                />

                <Route
                  path='/story/:projectId/loading/:loadingId/update'
                  element={
                    <AuthRoute>
                      <LoadingImageEditPage />
                    </AuthRoute>
                  }
                />

                {/* 작품 관리 - 에피소드 수정 페이지 */}
                <Route
                  path='story/:projectId/episode/:episodeId/update'
                  element={
                    <AuthRoute>
                      <EpisodeEditPage />
                    </AuthRoute>
                  }
                />

                {/* 작품 - 스크립트 */}

                <Route
                  path='sheet/:project_id/contents/:episode_id'
                  element={
                    <AuthRoute>
                      <ScriptSheet />
                    </AuthRoute>
                  }
                />

                {/* 작품 - 리소스. 미니컷 수정*/}
                <Route
                  path='story/:projectId/resource/minicut/:minicutId/update'
                  element={
                    <AuthRoute>
                      <MinicutUpdatePage />
                    </AuthRoute>
                  }
                />
                {/* 작품 - 라이브 오브제 */}
                <Route
                  path='story/:projectId/resource/liveObject/:liveId/update'
                  element={
                    <AuthRoute>
                      <LiveObjectUpdate />
                    </AuthRoute>
                  }
                />
                {/* 작품 - 리소스. 일러스트 수정 */}
                <Route
                  path='story/:projectId/resource/illust/:illustId/update'
                  element={
                    <AuthRoute>
                      <IllustUpdatePage />
                    </AuthRoute>
                  }
                />
                {/* 작품 - 라이브 일러스트 */}
                <Route
                  path='story/:projectId/resource/liveIllust/:liveId/update'
                  element={
                    <AuthRoute>
                      <LiveIllustUpdate />
                    </AuthRoute>
                  }
                />
                <Route
                  path='story/:projectId/resource/liveBackground/:liveId/update'
                  element={
                    <AuthRoute>
                      <LiveBackgroundUpdate />
                    </AuthRoute>
                  }
                />
                {/* 작품 - 로딩 이미지 수정 */}
                <Route
                  path='story/:projectId/epiLoading/:loadingId/update'
                  element={
                    <AuthRoute>
                      <LoadingImageEditPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='notice/:projectId/:noticeId'
                  element={
                    <AuthRoute>
                      <NewNoticeInputPage />
                    </AuthRoute>
                  }
                />

                {/* 작품(오토메) - DLC 수정 */}
                <Route
                  path='story/:projectId/dlc/:dlcId/update'
                  element={
                    <AuthRoute>
                      <DLCUpdatePage />
                    </AuthRoute>
                  }
                />

                {/* Character Update */}
                <Route
                  path='story/:projectId/character/character/:characterId/update'
                  element={
                    <AuthRoute>
                      <CharacterEditPage />
                    </AuthRoute>
                  }
                />
                {/* 등장인물 능력 수정 */}
                <Route
                  path='story/:projectId/character/character/:profile_id/:ability_id/update'
                  element={
                    <AuthRoute>
                      <AbilityEditPage />
                    </AuthRoute>
                  }
                />
                {/* 작품(오토메) - DLC 에피소드 수정 */}
                <Route
                  path='story/:projectId/dlc/:dlcId/update/:episodeId'
                  element={
                    <AuthRoute>
                      <DLCEpisodeUpdatePage />
                    </AuthRoute>
                  }
                />

                {/* 플랫폼 관리 - 작품 배포 */}
                {/* {['/deploy', '/deploy/:pageNum'].map(path => ( */}
                <Route
                  path='/deploy'
                  element={
                    <AuthRoute>
                      <StoryDeployListPage />
                    </AuthRoute>
                  }
                />
                {/* ))} */}
                {/* 플랫폼 관리 - 말풍선 세트 */}
                <Route
                  path='/bubble'
                  element={
                    <AuthRoute>
                      <BubbleMainListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='/bubble/:id/:type'
                  element={
                    <AuthRoute>
                      <BubbleSetTabsPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='/sprite'
                  element={
                    <AuthRoute>
                      <SpriteLIstPage />
                    </AuthRoute>
                  }
                />

                {/* 플랫폼 - 다국어 설정 */}
                <Route
                  path='/multilang'
                  element={
                    <AuthRoute>
                      <LocalizingPage />
                    </AuthRoute>
                  }
                />
                {/* 플랫폼 - 재화 관리 */}
                <Route
                  path='/currencyMaster'
                  element={
                    <AuthRoute>
                      <CurrencyListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='/currencyMaster/:currencyId'
                  element={
                    <AuthRoute>
                      <CurrencyInputPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='/systemSound'
                  element={
                    <AuthRoute>
                      <SystemSoundListPage />
                    </AuthRoute>
                  }
                />

                {/* 운영 관리 */}
                <Route
                  path='platformimg'
                  element={
                    <AuthRoute>
                      <PlatformImageListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='platformSound'
                  element={
                    <AuthRoute>
                      <PlatformSoundListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='notice'
                  element={
                    <AuthRoute>
                      <NoticeListPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='notice/:noticeId/update'
                  element={
                    <AuthRoute>
                      <NewNoticeInputPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='promotion'
                  element={
                    <AuthRoute>
                      <PromotionListPage />
                      {/* <PromotionList /> */}
                    </AuthRoute>
                  }
                />
                <Route
                  path='promotion/:promotionId'
                  element={
                    <AuthRoute>
                      <PromotionInputPage />
                    </AuthRoute>
                  }
                />

                {/* 커밍순 관리 */}
                <Route
                  path='comingSoon'
                  element={
                    <AuthRoute>
                      <ComingSoonListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='comingSoon/:comingId'
                  element={
                    <AuthRoute>
                      <ComingSoonInputPage />
                    </AuthRoute>
                  }
                />
                {/* 유저 관리 */}
                <Route
                  path='user'
                  element={
                    <AuthRoute>
                      <UserListPage />
                    </AuthRoute>
                  }
                />
                {/*신규 작업 */}
                {/*<Route*/}
                {/*  path='coupon'*/}
                {/*  element={*/}
                {/*    <AuthRoute>*/}
                {/*      <CouponListPage />*/}
                {/*    </AuthRoute>*/}
                {/*  }*/}
                {/*/>*/}
                <Route
                  path='coupon/:couponId'
                  element={
                    <AuthRoute>
                      <CouponInputPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='coupon/:id/detail'
                  element={
                    <AuthRoute>
                      {/* <CouponDetailView /> */}
                      {/*<CouponDetailListPage />*/}
                    </AuthRoute>
                  }
                />
                {/* 환전 관리 */}
                <Route
                  path='exchange'
                  element={
                    <AuthRoute>
                      <ExchangeUpdatePage />
                    </AuthRoute>
                  }
                />
                {/* 출석 관리 */}
                <Route
                  path='attendance'
                  element={
                    <AuthRoute>
                      <AttendanceListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='attendance/:attendanceId'
                  element={
                    <AuthRoute>
                      <AttendanceInputPage />
                    </AuthRoute>
                  }
                />

                {/* 운영 - 문의조회 관리 */}
                <Route
                  path='contact'
                  element={
                    <AuthRoute>
                      <ContactUsList />
                    </AuthRoute>
                  }
                />
                <Route
                  path='survey'
                  element={
                    <AuthRoute>
                      <SurveyListPage />
                    </AuthRoute>
                  }
                />
                {/* 판매 관리 */}

                {/*<Route*/}
                {/*  path='inStore/:id'*/}
                {/*  element={*/}
                {/*    <AuthRoute>*/}
                {/*      <InAppStoreListPage />*/}
                {/*    </AuthRoute>*/}
                {/*  }*/}
                {/*/>*/}

                <Route
                  path='inStore/:id/:masterId'
                  element={
                    <AuthRoute>
                      <InAppStoreInputPage />
                    </AuthRoute>
                  }
                />

                {/* 판매 - 코인 상점 */}
                <Route
                  path='coinStore'
                  element={
                    <AuthRoute>
                      <CoinStoreList />
                    </AuthRoute>
                  }
                />
                <Route
                  path='coinStore/:coinId'
                  element={
                    <AuthRoute>
                      <CoinStoreInputPage />
                    </AuthRoute>
                  }
                />

                <Route
                  path='premium'
                  element={
                    <AuthRoute>
                      <PremiumPassListPage />
                    </AuthRoute>
                  }
                />
                {/* 판매 - 광고 관리 */}
                <Route
                  path='advertisement'
                  element={
                    <AuthRoute>
                      <AdvertisementSettingPage />
                    </AuthRoute>
                  }
                />
                {/* 판매 - 광고 보상 */}
                <Route
                  path='adCompensation'
                  element={
                    <AuthRoute>
                      <AdCompensationPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='ifyouPass'
                  element={
                    <AuthRoute>
                      <IfyouPassListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='/assets'
                  element={
                    <AuthRoute>
                      <AssetsPage />
                    </AuthRoute>
                  }
                />
                {/* 패키지 관리 */}
                <Route
                  path='package'
                  element={
                    <AuthRoute>
                      <PackageListPage />
                    </AuthRoute>
                  }
                />

                {/* 패키지 관리 - 패키지 상세 정보(앱>앱 상세보기) */}
                <Route
                  path='/package/:packageId/:type'
                  element={
                    <AuthRoute>
                      <PackageDetailPage />
                    </AuthRoute>
                  }
                />
                {/* 관리자 관리 */}
                <Route
                  path='manager'
                  element={
                    <AuthRoute>
                      <ManagerListPage />
                    </AuthRoute>
                  }
                />
                <Route
                  path='manager/:userId'
                  element={
                    <AuthRoute>
                      <ManagerDetailPage />
                    </AuthRoute>
                  }
                />
              </Routes>
            </BrowserRouter>
          </LocalizingContext.Provider>
        </CookiesProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};
export default App;
